import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as d3 from 'd3';
import { Subscription } from 'rxjs';
import { unsubscribeComponent } from '../../../shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { IRowData } from '../../../shared/models/list-models';
import { DashboardCircleService } from '../../../shared/services/dashboard-circle-service/dashboard-circle-service';
import { IColumn } from './../../../shared/models/list-models';

export interface IBarGraphData {
  group: any;
  value: number;
  tooltipData: any;
}

interface IxAxisRotation {
  labelAnchor: string;
  rotation: number;
}

enum VizDataType {
  Percent = 'Percent',
  Count = 'Count',
}

@Component({
  selector: 'network-mid-level-viz',
  template: require('./network-mid-level-viz.component.html'),
  styles: [require('./network-mid-level-viz.component.scss')],
})
@unsubscribeComponent
export class NetworkMidLevelVizComponent implements AfterViewInit, OnChanges {
  @ViewChild('viz', { static: false }) element: ElementRef;
  @Input() title: string;
  @Input() groupingData;
  @Input() hoveredTableRow: IBarGraphData;
  @Output() hoveredBar = new EventEmitter<any>();

  rowData: IRowData[][];
  vizGroupingKey: string;
  vizDataType: VizDataType;

  svg: d3.Selection<SVGElement, any, any, any>;
  div;
  x;
  y;
  yNoData;
  xAxis;
  yAxis;
  yAxisNoData;
  width: number;
  height: number;
  margin = { top: 45, right: 35, bottom: 65, left: 40 };
  htmlElement: HTMLElement;
  initialized: boolean = false;
  xAxisRotation: IxAxisRotation;
  graphData: IBarGraphData;
  average: number;
  colorArray: Array<{ regular: string; bright: string }>;
  vizTitle: d3.Selection<SVGGElement, any, any, any>;
  mouseTarget: d3.Selection<SVGGElement, any, any, any>;
  xAxisGroup: d3.Selection<SVGGElement, any, any, any>;
  yAxisGroup: d3.Selection<SVGGElement, any, any, any>;
  barGroup: d3.Selection<SVGGElement, any, any, any>;
  averageGroup: d3.Selection<SVGGElement, any, any, any>;
  rollupGroupingSub: Subscription;
  focusDataSub: Subscription;
  midLevelColumns: IColumn[];
  totalRow: number;

  constructor(private dashboardCircleService: DashboardCircleService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.groupingData && !changes.groupingData.firstChange) {
      const { rowData, columnData } = changes.groupingData.currentValue;
      this.rowData = rowData;
      this.midLevelColumns = columnData;
      this.vizGroupingKey = 'FOCUS';
      // re/calculate average based on groupingData & columns before updating graph
      this.average = this.rowData.length > 0 ? this.computeAverage(rowData) : 0;
      this.updateGraph(rowData, this.title);
    }

    if (changes.hoveredTableRow && this.hoveredTableRow) this.displayBar(this.hoveredTableRow.group);
    if (changes.hoveredTableRow && changes.hoveredTableRow.currentValue === null) {
      this.showAllBars();
      this.div.style('opacity', 0);
    }
  }

  ngAfterViewInit() {
    const { rowData, columnData } = this.groupingData;
    this.rowData = rowData;
    this.midLevelColumns = columnData;
    this.vizGroupingKey = 'FOCUS';
    // re/calculate average based on groupingData & columns before updating graph
    this.average = rowData.length > 0 ? this.computeAverage(rowData) : 0;
    this.buildSvgElement();
    this.buildGraph(this.formatData(rowData), this.title);
  }

  buildSvgElement(): void {
    this.htmlElement = this.element.nativeElement;
    this.width = this.htmlElement.offsetWidth - 10;
    this.height = this.htmlElement.offsetHeight - 30;
    this.svg = d3
      .select(this.htmlElement)
      .append('svg')
      .attr('width', this.width)
      .attr('height', this.height);

    this.mouseTarget = this.svg.append('g').attr('transform', `translate(${this.margin.left},0)`);
    this.xAxisGroup = this.svg.append('g');
    this.yAxisGroup = this.svg.append('g');
    this.barGroup = this.svg.append('g');
    this.vizTitle = this.svg.append('g');
    this.averageGroup = this.svg.append('g').attr('transform', `translate(${this.margin.left},0)`);
    this.initialized = true;
  }

  preGraphFormat(data: IBarGraphData[]): void {
    this.xAxisRotation = this.getLabelRotation(data);
    this.colorArray = this.dashboardCircleService.getColors(data);
    this.createScales(data);
  }

  formatData(rowData: IRowData[][]): IBarGraphData[] {
    return rowData.map(row => ({
      group: row[0].data,
      value: row[row.length - 1].data,
      tooltipData: JSON.parse(row[row.length - 1].meta).vizTooltipData,
    }));
  }

  computeAverage(rowData) {
    const { numTotal, denomTotal } = rowData.reduce(
      (totals: { numTotal: number; denomTotal: number }, colArr) => {
        const focus = colArr[colArr.length - 1];
        const { meta } = focus;
        const { num, denom } = JSON.parse(meta);
        totals.numTotal += +num;
        totals.denomTotal += +denom;
        return totals;
      },
      { numTotal: 0, denomTotal: 0 },
    );
    const quotient = numTotal / denomTotal;
    return quotient;
  }

  // this could ascending or decending ?
  sortData(data: IBarGraphData[]): void {
    data.sort((a, b) => {
      return b.value - a.value;
    });
  }

  createScales(data: IBarGraphData[]): void {
    const yAxisDomainMax: number = 1;

    this.x = d3
      .scaleBand()
      .domain(data.map((d: IBarGraphData) => d.group))
      .range([this.margin.left, this.width - this.margin.right])
      .paddingInner(0.3)
      .paddingOuter(0.7);

    this.y = d3
      .scaleLinear()
      .domain([0, yAxisDomainMax])
      .nice()
      .range([this.height - this.margin.bottom, this.margin.top]);

    // used for no data display
    this.yNoData = d3
      .scaleLinear()
      .domain([0, 5])
      .nice()
      .range([this.height - this.margin.bottom, this.margin.top]);
  }

  createAxes() {
    this.xAxis = g =>
      g
        .attr('transform', `translate(0,${this.height - this.margin.bottom})`)
        .attr('class', 'x-axis')
        .call(d3.axisBottom(this.x).tickSizeOuter(0))
        .call(g => g.select('.domain').remove());

    this.yAxis = g =>
      g
        .attr('transform', `translate(${this.margin.left},0)`)
        .attr('class', 'y-axis')
        .call(
          d3
            .axisLeft(this.y)
            .ticks(5)
            .tickSize(-this.width + this.margin.left + this.margin.right)
            .tickFormat(d3.format('.0%')),
        )
        .call(g => g.select('.domain').remove());

    this.yAxisNoData = g =>
      g
        .attr('transform', `translate(${this.margin.left},0)`)
        .attr('class', 'y-axis')
        .call(
          d3
            .axisLeft(this.yNoData)
            .ticks(5)
            .tickFormat(d => {
              return '';
            })
            .tickSize(-this.width + this.margin.left + this.margin.right),
        )
        .call(g => g.select('.domain').remove());
  }

  updateAxes(data: IBarGraphData[]): void {
    this.xAxisGroup.call(this.xAxis);
    data.length > 0 ? this.yAxisGroup.call(this.yAxis) : this.yAxisGroup.call(this.yAxisNoData);

    this.svg
      .select('.x-axis')
      .selectAll('text')
      .attr('transform', `rotate(${this.xAxisRotation.rotation})`)
      .style('text-anchor', this.xAxisRotation.labelAnchor);

    // format labels to fit width of bars
    this.formatLabels(data, this.x.bandwidth());
  }

  addTitle(addTransition: boolean, data: IBarGraphData[], title): void {
    this.vizTitle
      .selectAll('.viz-title')
      .remove()
      .exit();
    this.vizTitle
      .append('text')
      .attr('class', 'viz-title')
      .attr('x', 20)
      .attr('y', 15)
      .text(title)
      .style('opacity', 0)
      .transition()
      .duration(addTransition ? 1000 : 0)
      .style('opacity', 0.6);
  }

  buildGraph(data: IBarGraphData[], title) {
    this.sortData(data);
    this.preGraphFormat(data);

    this.createAxes();
    this.updateAxes(data);
    const addTransition = true;
    this.addTitle(addTransition, data, title);

    // build tooltip div
    this.div = d3
      .select(this.htmlElement)
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0);

    const bars = this.barGroup
      .selectAll('path')
      .data(data)
      .enter()
      .append('path')
      .attr('class', 'bar')
      .attr('d', (d: IBarGraphData) => {
        return this.rectanglesPreTransition(d);
      })
      .attr('fill', (d: IBarGraphData, i: number) => this.colorArray[i].regular);

    bars
      .transition()
      .duration(1000)
      .attr('d', (d: IBarGraphData) => {
        return this.rectanglesPostTransition(d);
      });

    const labels = this.xAxisGroup.selectAll('.tick').data(data);

    this.mouseTarget
      .append('rect')
      .attr('class', 'mouseTarget')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', this.width - this.margin.left)
      .attr('height', this.height)
      .style('opacity', 0);

    // on mouseover
    bars.on('mouseover', (d: IBarGraphData, i: number) => {
      this.shareHoveredBar(d);
      this.displayBar(d.group);
    });

    labels.on('mouseover', (d, i) => {
      this.shareHoveredBar(d);
      this.displayBar(d.group);
    });

    // on mouseout
    bars.on('mouseout', (d: IBarGraphData, i: number) => {
      this.shareHoveredBar(null);
    });

    labels.on('mouseout', (d: IBarGraphData, i: number) => {
      this.shareHoveredBar(null);
    });

    d3.selectAll('.mouseTarget').on('mouseout', () => {
      this.showAllBars();
      this.div.style('opacity', 0);
    });

    // only build average line if displaying data
    data.length > 0 ? this.buildAverageLine() : this.removeAverageLine();

    return this.svg.node();
  }

  graphHasData(data): boolean {
    return data.length > 0 ? true : false;
  }

  updateGraph(rowData: IRowData[][], title): void {
    const data = this.formatData(rowData);
    this.sortData(data);
    this.preGraphFormat(data);
    this.updateAxes(data);
    this.addTitle(true, data, title);
    this.div.style('opacity', 0);

    const bars: any = this.barGroup.selectAll('path').data(data);

    bars
      .enter()
      .append('path')
      .attr('class', 'bar')
      .merge(bars)
      .attr('d', (d: IBarGraphData) => {
        return this.rectanglesPreTransition(d);
      })
      .attr('fill', (d: IBarGraphData, i: number) => this.colorArray[i].regular);

    const updatedBars = this.barGroup.selectAll('path').data(data);

    updatedBars
      .transition()
      .duration(1000)
      .attr('d', d => {
        return this.rectanglesPostTransition(d);
      });

    bars.exit().remove();

    const labels = this.xAxisGroup.selectAll('.tick').data(data);

    updatedBars.on('mouseover', (d: IBarGraphData, i: number) => {
      this.shareHoveredBar(d);
      this.displayBar(d.group);
    });

    labels.on('mouseover', (d, i) => {
      this.shareHoveredBar(d);
      this.displayBar(d.group);
    });

    updatedBars.on('mouseout', (d: IBarGraphData, i: number) => {
      this.shareHoveredBar(null);
      this.div.style('opacity', 0);
    });

    labels.on('mouseout', (d: IBarGraphData, i: number) => {
      this.shareHoveredBar(null);
    });

    // only build average line if displaying data
    data.length > 0 ? this.buildAverageLine() : this.removeAverageLine();
  }

  rectanglesPreTransition(d: IBarGraphData): string {
    const rx = d.value > 0 ? 4 : 0;
    const ry = d.value > 0 ? 4 : 0;
    return `
      M${this.x(d.group)},${this.y(0)}
      a${rx},${ry} 0 0 1 ${rx},${-ry}
      h${this.x.bandwidth() - 2 * rx}
      a${rx},${ry} 0 0 1 ${rx},${ry}
      v${0}
      h${-this.x.bandwidth()}
      Z
    `;
  }

  rectanglesPostTransition(d: IBarGraphData): string {
    const rx = d.value > 0 ? 4 : 0;
    const ry = d.value > 0 ? 4 : 0;
    return `
      M${this.x(d.group)},${this.y(d.value) + ry}
      a${rx},${ry} 0 0 1 ${rx},${-ry}
      h${this.x.bandwidth() - 2 * rx}
      a${rx},${ry} 0 0 1 ${rx},${ry}
      v${this.height - this.margin.bottom - this.y(d.value) - ry}
      h${-this.x.bandwidth()}
      Z
    `;
  }

  getLabelRotation(data: IBarGraphData[]): IxAxisRotation {
    let rotation;
    const groupCount = data.length;
    const maxLength = 8;
    const labelAnchor = groupCount > maxLength ? 'start' : 'middle';
    if (groupCount > 100) rotation = 45;
    else rotation = groupCount > maxLength ? 15 : 0;
    return { labelAnchor, rotation };
  }

  // if data length greater than 24, replace text labels with circles.
  // if data is greater than 8, increase width since labels are on a slant.
  formatLabels(data: IBarGraphData[], width: number): void {
    this.removeCircleLabels();
    this.removeNoDataLabel();
    if (data.length >= 250) {
      this.removeTextLabels(16);
      this.formatSchoolTextLabels(width);
    } else if (data.length > 50) {
      this.removeTextLabels(4);
      this.formatSchoolTextLabels(width);
    } else if (data.length >= 24) {
      this.removeTextLabels(2);
      this.formatSchoolTextLabels(width);
    } else if (data.length > 8) {
      width = width - 5;
      this.formatTextLabels(width);
    } else if (data.length < 1) {
      this.addNoDataLabel();
    } else {
      this.formatTextLabels(width);
    }
  }

  // remove existing circles
  removeCircleLabels(): void {
    this.svg
      .selectAll('circle')
      .remove()
      .exit();
  }

  // remove text labels
  removeTextLabels(limit?: number): void {
    const nodes = d3.selectAll('.x-axis text').nodes();
    const filteredNodes = nodes.filter((_, i) => i % limit !== 0);
    if (limit) filteredNodes.forEach(node => d3.select(node).text(''));
    else nodes.forEach(node => d3.select(node).text(''));
  }

  // if text wider than bar, shorten and add ellipsis
  formatTextLabels(width: number): void {
    const nodes = d3.selectAll('.x-axis text').nodes();
    nodes.forEach((node: any) => {
      let text = node.textContent.toString();
      let nodeLength = node.getComputedTextLength();
      while (nodeLength > width && width > 30) {
        let tspan = d3.select(node);
        text = text.slice(0, -1);
        tspan.text(`${text}...`);
        nodeLength = tspan.node().getComputedTextLength();
      }
    });
  }

  formatSchoolTextLabels(width: number): void {
    const nodes = d3.selectAll('.x-axis text').nodes();
    nodes.forEach((node: any) => {
      let text = node.textContent.toString();
      let nodeLength = node.getComputedTextLength();
      if (nodeLength > width) {
        let tspan = d3.select(node);
        text = text.slice(0, 8);
        tspan.text(`${text}...`);
        nodeLength = tspan.node().getComputedTextLength();
      }
    });
  }

  addNoDataLabel(): void {
    d3.selectAll('.x-axis')
      .append('text')
      .attr('class', 'no-data-label')
      .attr('x', this.width / 2)
      .attr('y', 25)
      .text('No data found');
  }

  removeNoDataLabel(): void {
    d3.selectAll('.no-data-label').remove();
  }

  // adds circles to x-axis
  addCircleLabels(data: IBarGraphData[], width: number): void {
    this.xAxisGroup
      .selectAll('circle')
      .data(data)
      .enter()
      .append('circle')
      .attr('cx', d => {
        return this.x(d.group) + width / 2;
      })
      .attr('cy', 10)
      .attr('r', width / 2)
      .attr('fill', (d: IBarGraphData, i: number) => {
        return this.colorArray[i].regular;
      });
  }

  removeAverageLine(): void {
    this.averageGroup
      .selectAll('.avg-text')
      .remove()
      .exit();

    this.averageGroup
      .selectAll('.avg-line')
      .remove()
      .exit();
  }

  buildAverageLine(): void {
    this.removeAverageLine();

    const text = this.averageGroup
      .append('text')
      .attr('class', 'avg-text')
      .attr('x', this.width - this.margin.left - this.margin.right)
      .attr('y', this.y(this.average) + 3);

    const averageDisplayVal = `${(this.average * 100).toFixed(1)}%`;

    text
      .append('tspan')
      .text(averageDisplayVal)
      .style('opacity', 0)
      .transition()
      .duration(2000)
      .style('opacity', 1);

    const textWidth = text.node().getComputedTextLength() + 8;
    const avgLabelHeight = d3.select(this.y(this.average)).node();
    const avgLabelPosition = avgLabelHeight < 22 ? this.y(this.average) + 15 : this.y(this.average) - 15;

    text
      .append('tspan')
      .attr('class', 'avg-label')
      .attr('x', this.width - this.margin.left - this.margin.right)
      .attr('y', avgLabelPosition)
      .text('Average')
      .style('opacity', 0)
      .transition()
      .duration(2000)
      .style('opacity', 0.6);

    this.averageGroup
      .append('line')
      .attr('class', 'avg-line')
      .attr('x1', 1)
      .attr('y1', this.y(this.average))
      .attr('x2', this.width - this.margin.left - this.margin.right - textWidth)
      .attr('y2', this.y(this.average))
      .style('opacity', 0)
      .transition()
      .duration(2000)
      .style('opacity', 1);
  }

  displayBar(group: string) {
    d3.selectAll('.bar').attr('fill', (d: IBarGraphData, i: number) => {
      if (d.group === group) {
        this.displayTooltip(d.group, d.value, d.tooltipData, i);
        return this.colorArray[i].bright;
      } else {
        return this.dashboardCircleService.shadeRGBColor(this.colorArray[i].regular, 0.7);
      }
    });
  }

  showAllBars() {
    d3.selectAll('.bar').attr('fill', (d: IBarGraphData, i: number) => {
      return this.colorArray[i].regular;
    });
  }

  // TODO: Clean this up
  displayTooltip(group: string, value: number, tooltipData, i: number): void {
    const nodes = d3.selectAll('.bar').nodes();
    const tooltipDisplayVal = `${(value * 100).toFixed(1)}%`;
    nodes.forEach((node: any) => {
      const clientRect = node.getBoundingClientRect();
      const clientText = node.__data__.group;
      if (clientText === group) {
        this.div
          .html(
            tooltipData.show_tooltip
              ? `
      
              <div class="row">
                <span class="group">${tooltipData.groupLabel}:</span> 
                <span class="value">${tooltipData.groupValue}</span> 
              </div>
              
              <div class="row">
                <span class="group">${tooltipData.focusLabel}:</span>
                <span class="value">${tooltipDisplayVal}</span>
              </div>

              <div class="row">
                <span class="group"># students:</span>
                <span class="value">${tooltipData.numerator}</span>
              </div>

              <div class="row">
                <span class="group">Total # students:</span> 
                <span class="value">${tooltipData.denominator} </span> 
              </div>

             `
              : `
              <div class="row">
                <span class="group">${tooltipData.groupLabel}:</span> 
                <span class="value">${tooltipData.groupValue}</span> 
              </div>
            
              <div class="row">
                <span class="group">${tooltipData.focusLabel}:</span>
                <span class="value">${tooltipDisplayVal}</span>
              </div>
            `,
          )
          .style('font-size', '12px')
          .style('left', clientRect.left + clientRect.width / 2 + 'px')
          .style('top', clientRect.top - 65 + 'px')
          .style('opacity', 0.9);
      }
    });
  }

  shareHoveredBar(bar): void {
    this.hoveredBar.emit(bar);
  }
}
